import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { Inbox, Zap } from 'lucide-react';
import { DataTable, DataTablePagination } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { Button } from '@/components/ui/button';
import { useKeepPageInRange } from '@/hooks/useKeepPageInRange';
import { cn } from '@/lib/utils';
import { PCMP_PERMISSION } from '../../api/pcmpAccess.api';
import { pcmpRequestQueueQueryOptions } from '../../api/pcmpRequests.api';
import { PCMP_QUEUE_PAGE_SIZE_OPTIONS } from '../../constants/pcmpQueue.constants';
import {
  PCMP_REQUEST_EMPTY_MESSAGE,
  PCMP_REQUEST_VIEW_LABELS,
} from '../../constants/pcmpRequest.constants';
import type { PcmpRequestFilterController } from '../../hooks/usePcmpRequestFilters';
import { usePcmpPermissions } from '../../hooks/usePcmpPermissions';
import { usePcmpRowSelection } from '../../hooks/usePcmpRowSelection';
import type {
  PcmpRequestCounts,
  PcmpRequestQueueItem,
  PcmpRequestQueueParams,
} from '../../types/pcmpRequest.types';
import { PcmpSelectionBar } from '../PcmpBatch/PcmpSelectionBar';
import { pcmpSelectColumn } from '../PcmpQueue/pcmpSelectColumn';
import { getPcmpRequestColumns } from './PcmpRequestColumns';
import { PcmpRequestRowActions } from './PcmpRequestRowActions';
import { PcmpRequestSubmitDialog, type PcmpRequestSubmitTarget } from './PcmpRequestSubmitDialog';

interface PcmpRequestListViewProps {
  controller: PcmpRequestFilterController;
  params: PcmpRequestQueueParams;
  counts: PcmpRequestCounts | undefined;
}

/** The Standard and Disbursed tabs: pick proposals and raise a request on them. */
export function PcmpRequestListView({ controller, params, counts }: PcmpRequestListViewProps) {
  const router = useRouter();
  const { tab, limit, appliedFilters } = controller;
  const { can } = usePcmpPermissions();
  const canRaise = can(PCMP_PERMISSION.REQUEST_CREATE, PCMP_PERMISSION.ACT);

  const selection = usePcmpRowSelection(JSON.stringify([tab, appliedFilters]));
  const [submitTarget, setSubmitTarget] = useState<PcmpRequestSubmitTarget | null>(null);

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    pcmpRequestQueueQueryOptions(params),
  );

  useKeepPageInRange(params.page, data?.meta.totalPages, controller.setPage);

  const openProposal = useCallback(
    (item: PcmpRequestQueueItem) => {
      void router.navigate({
        to: '/pcmp/proposals/$proposalId',
        params: { proposalId: item.proposalId },
      });
    },
    [router],
  );

  const rows = useMemo(() => data?.data ?? [], [data]);

  // Every row seen, by id: a ticked row may be on another page, and a Payment
  // Return needs each one's ref and channel.
  const [seenRows, setSeenRows] = useState(rows);
  const [knownRows, setKnownRows] = useState(() => new Map<number, PcmpRequestQueueItem>());
  if (seenRows !== rows) {
    setSeenRows(rows);
    setKnownRows((prev) => {
      const next = new Map(prev);
      for (const row of rows) next.set(row.proposalId, row);
      return next;
    });
  }

  const columns = getPcmpRequestColumns({
    leading: canRaise ? [pcmpSelectColumn<PcmpRequestQueueItem>({ selection })] : [],
    renderActions: (item) => <PcmpRequestRowActions item={item} />,
  });

  const total = data?.meta.total ?? 0;
  const tabAmount =
    (params.tab === 'DISBURSED' ? counts?.disbursedAmount : counts?.standardAmount) ?? 0;
  const summary = selection.summarize(total, tabAmount);
  const isPageSelected =
    rows.length > 0 && rows.every((row) => selection.isSelected(row.proposalId));

  const refNoOf = (proposalId: number) => knownRows.get(proposalId)?.refNo;

  const startSubmit = () =>
    setSubmitTarget({
      count: summary.count,
      amount: summary.amount,
      appliesTo: params.tab === 'DISBURSED' ? 'DISBURSED' : 'STANDARD',
      items: selection.allMatching
        ? undefined
        : selection.pickedIds.flatMap((id) => {
            const row = knownRows.get(id);
            return row
              ? [{ proposalId: id, refNo: row.refNo, paymentSource: row.paymentSource }]
              : [];
          }),
      selection: selection.allMatching
        ? {
            selectAllMatching: true,
            filters: appliedFilters,
            excludedProposalIds: selection.pickedIds,
          }
        : { proposalIds: selection.pickedIds },
    });

  return (
    <>
      {canRaise ? (
        <PcmpSelectionBar
          selection={selection}
          summary={summary}
          selectAll={{ total, amount: tabAmount, isPageSelected }}
          hint="Tick proposals to raise a request on them."
        >
          <Button
            type="button"
            size="sm"
            onClick={startSubmit}
            disabled={summary.count === 0}
            className="gap-1.5"
          >
            <Zap className="size-3.5" />
            Raise request
          </Button>
        </PcmpSelectionBar>
      ) : null}

      {error ? (
        <ErrorState
          error={error}
          fallbackTitle={`Could not load ${PCMP_REQUEST_VIEW_LABELS[tab]} proposals`}
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          stickyFirstColumn
          fillHeight
          className={cn('lg:min-h-0 lg:flex-1', isFetching && !isLoading && 'opacity-70')}
          emptyIcon={Inbox}
          emptyMessage={PCMP_REQUEST_EMPTY_MESSAGE[tab]}
          skeletonRowCount={Math.min(limit, 15)}
          getRowClassName={(item) =>
            canRaise && selection.isSelected(item.proposalId) ? 'bg-primary/5' : undefined
          }
          onRowClick={openProposal}
        />
      )}

      {data?.meta && !error ? (
        <DataTablePagination
          page={data.meta.page}
          totalPages={data.meta.totalPages}
          total={data.meta.total}
          limit={data.meta.limit}
          onPageChange={controller.setPage}
          limitSelect={{ options: PCMP_QUEUE_PAGE_SIZE_OPTIONS, onChange: controller.setLimit }}
        />
      ) : null}

      {submitTarget ? (
        <PcmpRequestSubmitDialog
          target={submitTarget}
          refNoOf={refNoOf}
          onSubmitted={selection.clear}
          onClose={() => setSubmitTarget(null)}
        />
      ) : null}
    </>
  );
}
